import styled, { css } from 'styled-components';

import { TransientProps } from '@common/types';

type FieldWidth = 'full' | 'half' | 'third';

const CURSOR_NOT_ALLOWED = 'not-allowed';

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

export const FieldsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
`;

type FieldWrapperProps = TransientProps<{
  width: FieldWidth;
}>;

export const FieldWrapper = styled.div<FieldWrapperProps>`
  ${({ $width }) => css`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;

    ${$width === 'half' &&
    css`
      @media (min-width: 768px) {
        width: calc(50% - 0.5rem);
      }
    `}

    ${$width === 'third' &&
    css`
      @media (min-width: 768px) {
        width: calc(33.333% - 0.667rem);
      }
    `}
  `}
`;

type FieldLabelProps = TransientProps<{
  required?: boolean;
}>;

export const FieldLabel = styled.label<FieldLabelProps>`
  ${({ theme, $required }) => css`
    font-size: 0.875rem;
    font-weight: 500;
    color: ${theme.colors.foreground[100]};

    ${$required &&
    css`
      &::after {
        content: ' *';
        color: ${theme.colors.error[100]};
      }
    `}
  `}
`;

export const HelpText = styled.span`
  ${({ theme }) => css`
    font-size: 0.75rem;
    color: ${theme.colors.foreground[60]};
    margin-top: 0.25rem;
  `}
`;

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

type RadioLabelProps = TransientProps<{
  disabled?: boolean;
}>;

export const RadioLabel = styled.label<RadioLabelProps>`
  ${({ theme, $disabled }) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: ${$disabled ? CURSOR_NOT_ALLOWED : 'pointer'};
    opacity: ${$disabled ? 0.6 : 1};

    input[type='radio'] {
      width: 1.25rem;
      height: 1.25rem;
      accent-color: ${theme.colors.brand.primary.default};
    }

    span {
      font-size: 0.875rem;
      color: ${theme.colors.foreground[100]};
    }
  `}
`;

export const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

type CheckboxLabelProps = TransientProps<{
  disabled?: boolean;
}>;

export const CheckboxLabel = styled.label<CheckboxLabelProps>`
  ${({ theme, $disabled }) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: ${$disabled ? CURSOR_NOT_ALLOWED : 'pointer'};
    opacity: ${$disabled ? 0.6 : 1};

    input[type='checkbox'] {
      width: 1.25rem;
      height: 1.25rem;
      accent-color: ${theme.colors.brand.primary.default};
    }

    span {
      font-size: 0.875rem;
      color: ${theme.colors.foreground[100]};
    }
  `}
`;

export const FileInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const FilePreview = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: ${theme.colors.background[80]};
    border-radius: ${theme.radii.default};

    span {
      font-size: 0.875rem;
      color: ${theme.colors.foreground[100]};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `}
`;

export const ImagePreview = styled.div`
  ${({ theme }) => css`
    position: relative;
    width: fit-content;

    img {
      max-width: 200px;
      max-height: 200px;
      border-radius: ${theme.radii.default};
      object-fit: cover;
    }

    button {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      background: ${theme.colors.background[100]};
      border-radius: 50%;
    }
  `}
`;

type ImageDropzoneProps = TransientProps<{
  disabled?: boolean;
}>;

export const ImageDropzone = styled.div<ImageDropzoneProps>`
  ${({ theme, $disabled }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 2rem;
    border: 2px dashed ${theme.colors.base.border};
    border-radius: ${theme.radii.default};
    cursor: ${$disabled ? CURSOR_NOT_ALLOWED : 'pointer'};
    opacity: ${$disabled ? 0.6 : 1};
    transition: ${theme.transition.default};

    &:hover {
      border-color: ${$disabled
        ? theme.colors.base.border
        : theme.colors.brand.primary.default};
    }

    svg {
      color: ${theme.colors.foreground[40]};
    }

    span {
      font-size: 0.875rem;
      color: ${theme.colors.foreground[60]};
      text-align: center;
    }
  `}
`;

export const HeaderElement = styled.h3`
  ${({ theme }) => css`
    font-size: 1.25rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    margin: 1rem 0 0.5rem;
    width: 100%;
  `}
`;

export const ParagraphElement = styled.p`
  ${({ theme }) => css`
    font-size: 0.875rem;
    color: ${theme.colors.foreground[80]};
    line-height: 1.5;
    width: 100%;
  `}
`;

export const SubmitWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-top: 1rem;

  button {
    width: 100%;
  }
`;

export const SuccessMessage = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 2rem;
    text-align: center;

    svg {
      color: ${theme.colors.success[100]};
    }

    h3 {
      font-size: 1.25rem;
      color: ${theme.colors.foreground[100]};
    }

    p {
      color: ${theme.colors.foreground[80]};
    }
  `}
`;
